import { useState, useEffect } from "react";
import { getAPIURL } from "../../../services/helper";
import http from "../../../services/http";
import { Spinner, Container, Card, Table } from "reactstrap";
import EODHeader from "./EODHeader";

const EODSummary = ({ register_id }) => {
  const api_url = getAPIURL();

  const [register, setRegister] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRegisterReport();
  }, []);

  const fetchRegisterReport = async () => {
    setLoading(true);
    try {
      const { data } = await http.get(
        `${api_url}/reports/close/detail/${register_id}`
      );
      setRegister(data);
      setTransactions(data.transactions || []);
    } catch (error) {
      console.error("Error fetching register report:", error);
    }
    setLoading(false);
  };

  const getPaymentSummary = (transactions) => {
    const paymentSummary = {};
    transactions.forEach((t) => {
      if (!paymentSummary[t.payment_type]) {
        paymentSummary[t.payment_type] = { count: 0, amount: 0 };
      }
      paymentSummary[t.payment_type].count += 1;
      paymentSummary[t.payment_type].amount += parseFloat(t.order_total || 0);
    });
    return paymentSummary;
  };

  const calculateFullSummary = (transactions) => {
    let totalWithoutTax = 0;
    let totalWithTax = 0;
    let totalTax = 0;
    let totalDiscount = 0;
    let totalOrders = 0;

    transactions.forEach((t) => {
      const orderTotal = parseFloat(t.order_total || 0);
      const taxCollected = parseFloat(t.tax_collected || 0);
      const discount = parseFloat(t.discount || 0);

      totalWithoutTax += orderTotal - taxCollected;
      totalWithTax += orderTotal;
      totalTax += taxCollected;
      totalDiscount += discount;
      totalOrders += 1;
    });

    return {
      totalWithoutTax,
      totalWithTax,
      totalTax,
      totalDiscount,
      totalOrders,
    };
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner color="primary" size="lg" />
        <p>Loading Register Report...</p>
      </div>
    );
  }

  if (!register) {
    return (
      <div className="text-center mt-5">
        <p>No register report found.</p>
      </div>
    );
  }

  const paymentSummary = getPaymentSummary(transactions);
  const fullSummary = calculateFullSummary(transactions);

  const totalPaymentAmount = Object.values(paymentSummary).reduce(
    (sum, p) => sum + p.amount,
    0
  );
  const totalPaymentCount = Object.values(paymentSummary).reduce(
    (sum, p) => sum + p.count,
    0
  );

  return (
    <div className="page-body">
      <Container className="mt-4">
        {/* Header */}
        <EODHeader
          closed_at={register.closed_at}
          printLink={`/print/reports/eod-summary/${register_id}`}
        />

        {/* Summary Info */}
        <Card className="p-3 mb-4 shadow-sm">
          <div className="d-flex justify-content-between">
            <h5>EOD#: {register.station_close_number}</h5>
            <h5>Grand Total: ${fullSummary.totalWithTax.toFixed(2)}</h5>
          </div>
        </Card>

        {/* Payment Type Summary */}
        <Card className="p-3 mb-4 shadow-sm">
          <h5>Pay Type Summary</h5>
          <Table bordered size="sm">
            <thead>
              <tr>
                <th>Pay Type</th>
                <th>Trans Count</th>
                <th>Trans Amt</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(paymentSummary).map(([payType, summary]) => (
                <tr key={payType}>
                  <td>{payType}</td>
                  <td>{summary.count}</td>
                  <td>${summary.amount.toFixed(2)}</td>
                </tr>
              ))}
              <tr>
                <td>
                  <strong>Total</strong>
                </td>
                <td>
                  <strong>{totalPaymentCount}</strong>
                </td>
                <td>
                  <strong>${totalPaymentAmount.toFixed(2)}</strong>
                </td>
              </tr>
            </tbody>
          </Table>
        </Card>

        {/* Transaction Type Summary */}
        <Card className="p-3 mb-4 shadow-sm">
          <h5>Transaction Summary</h5>
          <Table bordered size="sm">
            <thead>
              <tr>
                <th>Trans. Type</th>
                <th>Trans Count</th>
                <th>Trans Amt</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Sale</td>
                <td>{fullSummary.totalOrders}</td>
                <td>${fullSummary.totalWithTax.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Return</td>
                <td>0</td>
                <td>$0.00</td>
              </tr>
              <tr>
                <td>Tax</td>
                <td>0</td>
                <td>${fullSummary.totalTax.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Discount</td>
                <td>0</td>
                <td>${fullSummary.totalDiscount.toFixed(2)}</td>
              </tr>
              <tr>
                <td>
                  <strong>Total</strong>
                </td>
                <td>
                  <strong>{fullSummary.totalOrders}</strong>
                </td>
                <td>
                  <strong>${fullSummary.totalWithTax.toFixed(2)}</strong>
                </td>
              </tr>
            </tbody>
          </Table>
        </Card>

        {/* Final Sales */}
        <Card className="p-3 shadow-sm">
          <h5>Sales Summary</h5>
          <Table bordered size="sm">
            <tbody>
              <tr>
                <td>Total Sales (Without Tax)</td>
                <td>${fullSummary.totalWithoutTax.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Total Sales (With Tax)</td>
                <td>${fullSummary.totalWithTax.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Sale Tax Amount</td>
                <td>${fullSummary.totalTax.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Total Discount Given</td>
                <td>${fullSummary.totalDiscount.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Number of Orders</td>
                <td>{fullSummary.totalOrders}</td>
              </tr>
            </tbody>
          </Table>
        </Card>
      </Container>
    </div>
  );
};

export default EODSummary;
